import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react'
import {useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import type {BookAppointmentRequest} from '@generated/models'
import {type Questionnaire} from '@common/models'
import {
  useCustomErrorMessage,
  yymmToYearMonth,
  currentYyMm,
  getCalendarMatrix,
  buildSlotsByDate,
} from '@common/utils'
import {formatDateLegend, getTimeZoneNotice} from '@common/utils/dates'

import type {PreQuestionnaireAnswers} from './PreQuestionnaireRenderer'
import {
  isRequiredPreqComplete,
  toPreQuestionnaireLiteralAnswers,
} from './preQuestionnaireAnswers'
import {BookingsHeader} from './BookingsHeader'
import {CalendarPanel} from './CalendarPanel'
import {DayTimePanel} from './DayTimePanel'
import {DetailsForm, type BookingFormValues} from './DetailsForm'
import {ConfirmBookingModal} from './ConfirmBookingModal'
import {SlotTakenModal} from './SlotTakenModal'
import {ConfirmationView} from './ConfirmationView'
import {FooterInfo} from './FooterInfo'
import {PoweredBy} from './PoweredBy'
import {
  PageWrapper,
  Grid,
  Column,
  SelectedGlobal,
} from './BookingsPortalPage.styled'

import {Typography} from '~/components/general/typography/Typography'
import {Container} from '~/components/general/container/Container'
import {Card} from '~/components/general/card/Card'
import {Button} from '~/components/general/button/Button'
import {ProcessingOverlay} from '~/components/general/processingOverlay/ProcessingOverlay'
import {
  useGetBookingsPortalServiceInfo,
  useGetBookingsPortalAvailabilities,
  useBookingsPortalBookAppointment,
} from '~/api/bookingsPortal'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {useWeekdayShorts} from '~/hooks/useWeekdayShorts'
import {useMonthNavigation} from '~/hooks/useMonthNavigation'

export const BookingsPortalPage = () => {
  const {t, i18n} = useTranslation<string>()
  const {partnerServiceId = ''} = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const rawMonthSpec = searchParams.get('m') || 'current'
  const apiMonthSpec = rawMonthSpec === 'current' ? 'FIRST' : rawMonthSpec
  const effectiveYyMm =
    rawMonthSpec === 'current' ? currentYyMm() : rawMonthSpec

  const [form, setForm] = useState<BookingFormValues>({
    firstName: '',
    secondName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
  })
  const [detailsValid, setDetailsValid] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [preqAnswers, setPreqAnswers] = useState<PreQuestionnaireAnswers>({})
  const [confirmed, setConfirmed] = useState(false)
  const [confirmedDateLegend, setConfirmedDateLegend] = useState<string>('')
  const [confirmedTime, setConfirmedTime] = useState<string>('')
  const [slotTakenOpen, setSlotTakenOpen] = useState(false)
  const [hasAutoRetriedServiceInfo, setHasAutoRetriedServiceInfo] =
    useState(false)

  const {
    data: info,
    error: serviceInfoError,
    isError: isServiceInfoError,
    isPending: isServiceInfoPending,
    isFetching: isFetchingServiceInfo,
    isLoadingError: isServiceInfoLoadingError,
    isRefetchError: isServiceInfoRefetchError,
    refetch: refetchServiceInfo,
  } = useGetBookingsPortalServiceInfo(partnerServiceId)
  const {
    data: avail,
    refetch: refetchAvail,
    isLoading: isLoadingAvailabilities,
    isFetching: isFetchingAvailabilities,
  } = useGetBookingsPortalAvailabilities(partnerServiceId, apiMonthSpec)

  const preQuestionnaire = info?.preQuestionnaire
  const hasServiceInfo = !!info

  const logServiceInfoRetryFailure = useCallback(
    (error: Error) => {
      console.error('Failed to load bookings portal service info after retry', {
        partnerServiceId,
        error,
        isLoadingError: isServiceInfoLoadingError,
        isRefetchError: isServiceInfoRefetchError,
      })
    },
    [partnerServiceId, isServiceInfoLoadingError, isServiceInfoRefetchError],
  )

  const handleRefetchServiceInfo = useCallback(async () => {
    const result = await refetchServiceInfo()
    if (result.error) {
      logServiceInfoRetryFailure(result.error)
    }
  }, [refetchServiceInfo, logServiceInfoRetryFailure])

  // Retry once when the first load fails. Without this, a missing `info` is
  // treated as "no pre-appointment survey" and the booking can still submit.
  useEffect(() => {
    if (
      !isServiceInfoError ||
      hasAutoRetriedServiceInfo ||
      isFetchingServiceInfo
    ) {
      return
    }
    setHasAutoRetriedServiceInfo(true)
    void handleRefetchServiceInfo()
  }, [
    isServiceInfoError,
    hasAutoRetriedServiceInfo,
    isFetchingServiceInfo,
    handleRefetchServiceInfo,
  ])

  useEffect(() => {
    if (!isServiceInfoRefetchError || !serviceInfoError) return
    logServiceInfoRetryFailure(serviceInfoError)
  }, [isServiceInfoRefetchError, serviceInfoError, logServiceInfoRetryFailure])

  const {mutate: bookAppointment, isPending: isBookAppointmentPending} =
    useBookingsPortalBookAppointment(partnerServiceId, apiMonthSpec)

  const {addNotification} = useContext(NotificationContext)
  const getCustomErrorMessage = useCustomErrorMessage()

  const days = useMemo(() => avail?.days ?? [], [avail?.days])
  const slotsByDate = useMemo(() => buildSlotsByDate(days), [days])

  useEffect(() => {
    const first = days.find((d) => (d.slots?.length || 0) > 0)
    if (first) {
      setSelectedDate(first.date)
    } else {
      const {year, month} = yymmToYearMonth(effectiveYyMm)
      const firstDayOfMonth = `${year}/${String(month).padStart(2, '0')}/01`
      setSelectedDate(firstDayOfMonth)
    }
    setSelectedTime(null)
  }, [effectiveYyMm, days])

  const didSyncMonthFromApiRef = useRef(false)
  useEffect(() => {
    if (!avail) return
    if (didSyncMonthFromApiRef.current) return
    if (rawMonthSpec !== 'current') {
      didSyncMonthFromApiRef.current = true
      return
    }
    let yymm: string | undefined = avail?.yearMonth
    if (!yymm) {
      const firstDay = avail.days?.[0]?.date
      if (firstDay) {
        const [yyyyStr, mmStr] = firstDay.split('/')
        const yy = String(Number(yyyyStr)).slice(-2)
        const mm = String(Number(mmStr)).padStart(2, '0')
        yymm = `${yy}${mm}`
      }
    }
    if (yymm && rawMonthSpec !== yymm) {
      setSearchParams({m: yymm})
    }
    didSyncMonthFromApiRef.current = true
  }, [avail, rawMonthSpec, setSearchParams])

  const requiredPreqOk = useMemo(() => {
    if (!preQuestionnaire) return true
    return isRequiredPreqComplete(preQuestionnaire, preqAnswers)
  }, [preQuestionnaire, preqAnswers])

  const canSubmitAll = detailsValid && requiredPreqOk && hasServiceInfo
  const showServiceInfoError =
    !hasServiceInfo && hasAutoRetriedServiceInfo && isServiceInfoError

  const headerSubtitle = useMemo(() => {
    if (!info) return ''
    const {partner, partnerService} = info
    return `${partnerService.name}${
      partner.address ? ` • ${partner.address}` : ''
    }`
  }, [info])

  const {gotoPrevMonth, gotoNextMonth, monthLabel, disablePrev} =
    useMonthNavigation({
      currentYyMm: effectiveYyMm,
      onMonthChange: (yyMm) => setSearchParams({m: yyMm}),
    })

  const timeZoneNotice = useMemo(
    () => getTimeZoneNotice(info?.partner.timeZoneId, t),
    [info?.partner.timeZoneId, t],
  )

  const weekdayShorts = useWeekdayShorts(i18n.language)

  const selectedDateLegend = useMemo(
    () => formatDateLegend(selectedDate || undefined),
    [selectedDate],
  )

  const selectSlotAndBook = useCallback(
    (
      date: string,
      time: string,
      effectiveQuestionnaire: Questionnaire | undefined,
    ) => {
      if (!detailsValid || !hasServiceInfo) return
      if (effectiveQuestionnaire) {
        if (!isRequiredPreqComplete(effectiveQuestionnaire, preqAnswers)) {
          return
        }
      }
      const answersLiteral = effectiveQuestionnaire
        ? toPreQuestionnaireLiteralAnswers(effectiveQuestionnaire, preqAnswers)
        : []

      const requestBody: BookAppointmentRequest = {
        date,
        time,
        firstName: form.firstName,
        secondName: form.secondName,
        email: form.email,
        phone: form.phone.trim(),
        dateOfBirth: form.dateOfBirth,
      }
      if (answersLiteral.length > 0) {
        requestBody.answers = answersLiteral
      }

      bookAppointment(requestBody, {
        onSuccess: () => {
          setConfirmed(true)
        },
        onError: (error: Error) => {
          const msg = error?.message
          if (
            msg === 'SLOT_NOT_AVAILABLE' ||
            msg === 'CONCURRENT_MODIFICATION'
          ) {
            setSlotTakenOpen(true)
            setSelectedTime(null)
            refetchAvail()
            return
          }
          const friendly = getCustomErrorMessage(error) || error.message
          addNotification(friendly, NotificationType.Error)
        },
      })
    },
    [
      detailsValid,
      hasServiceInfo,
      preqAnswers,
      form.firstName,
      form.secondName,
      form.email,
      form.phone,
      form.dateOfBirth,
      bookAppointment,
      getCustomErrorMessage,
      addNotification,
      refetchAvail,
    ],
  )

  const handleConfirmBooking = useCallback(() => {
    if (!selectedDate || !selectedTime) return
    if (!canSubmitAll) {
      setConfirmOpen(false)
      return
    }
    setConfirmedDateLegend(selectedDateLegend)
    setConfirmedTime(selectedTime)
    selectSlotAndBook(selectedDate, selectedTime, preQuestionnaire)
    setConfirmOpen(false)
  }, [
    selectedDate,
    selectedTime,
    canSubmitAll,
    selectedDateLegend,
    preQuestionnaire,
    selectSlotAndBook,
  ])

  const onClickBook = () => setConfirmOpen(true)

  const cells = useMemo(() => getCalendarMatrix(effectiveYyMm), [effectiveYyMm])
  const selectedDateSlots = selectedDate
    ? slotsByDate.get(selectedDate) || []
    : []

  const showProcessingOverlay =
    isServiceInfoPending ||
    (!hasServiceInfo && !showServiceInfoError) ||
    isFetchingAvailabilities ||
    isLoadingAvailabilities ||
    isBookAppointmentPending

  return (
    <PageWrapper>
      <ProcessingOverlay isVisible={showProcessingOverlay} />
      <BookingsHeader
        logoUrl={info?.partner.logoUrl}
        partnerName={info?.partner.name}
        partnerServiceName={info?.partnerService.name}
        headerSubtitle={headerSubtitle}
        confirmed={confirmed}
      />
      {!confirmed && hasServiceInfo && selectedDate && (
        <SelectedGlobal>
          <Typography size="xl" weight="bold">
            {selectedDateLegend}
          </Typography>
        </SelectedGlobal>
      )}

      {showServiceInfoError && (
        <Card>
          <Container direction="column" align="center" spacing={2}>
            <Typography textAlign="center">
              {t('Unable to load booking information. Please try again.')}
            </Typography>
            <Button
              onClick={() => {
                void handleRefetchServiceInfo()
              }}
              disabled={isFetchingServiceInfo}>
              {t('Retry')}
            </Button>
          </Container>
        </Card>
      )}

      {!confirmed && hasServiceInfo && (
        <Grid>
          <Column>
            <CalendarPanel
              dateTitle={t('DATE')}
              monthLabel={monthLabel}
              weekdayShorts={weekdayShorts}
              cells={cells}
              slotsByDate={slotsByDate}
              selectedDate={selectedDate}
              onSelectDate={(dateKey) => {
                setSelectedDate(dateKey)
                setSelectedTime(null)
              }}
              disablePrev={disablePrev}
              onPrevMonth={gotoPrevMonth}
              onNextMonth={gotoNextMonth}
            />
          </Column>
          <Column>
            <DayTimePanel
              title={t('TIME')}
              selectedDate={selectedDate}
              selectedDateSlots={selectedDateSlots}
              selectedTime={selectedTime}
              onSelectTime={(time) => setSelectedTime(time)}
              isLoading={isBookAppointmentPending}
              timeZoneNotice={timeZoneNotice}
              noAvailabilityText={t(
                'There is no availability on this date. Please choose another one.',
              )}
            />
          </Column>
        </Grid>
      )}
      {!confirmed && hasServiceInfo && (
        <Card>
          <Container direction="column" spacing={2}>
            <DetailsForm
              title={t('Add your details')}
              form={form}
              setForm={setForm}
              onValidityChange={setDetailsValid}
              preQuestionnaire={preQuestionnaire}
              preqAnswers={preqAnswers}
              onChangePreqAnswer={(qid, ans) =>
                setPreqAnswers((prev) => ({...prev, [qid]: ans}))
              }
              bookDisabled={
                !canSubmitAll ||
                !selectedDate ||
                !selectedTime ||
                isBookAppointmentPending
              }
              onClickBook={onClickBook}
              bookLabel={t('Book')}
            />
          </Container>
        </Card>
      )}
      {!confirmed && hasServiceInfo && (
        <ConfirmBookingModal
          isVisible={!!confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleConfirmBooking}
          isLoading={isBookAppointmentPending}
          header={t('Confirm your booking')}
          bodyIntro={t(
            'Please confirm your booking details. Make sure your email is your own and not a shared or generic email, as it will be used to match all your medical records in the system. If you used a shared email, please go back and correct it.',
          )}
          partnerServiceName={info?.partnerService.name}
          nameLine={`${form.firstName || '—'} ${form.secondName || ''}`}
          dateTimeLine={`${selectedDateLegend}${
            selectedTime ? ` - ${selectedTime}` : ''
          }`}
          email={form.email}
          phone={form.phone}
          cancelLabel={t('Cancel')}
          confirmLabel={t('Confirm booking')}
          timeZoneNotice={timeZoneNotice}
        />
      )}

      {!confirmed && (
        <SlotTakenModal
          isVisible={!!slotTakenOpen}
          onClose={() => setSlotTakenOpen(false)}
          onOk={async () => {
            setSlotTakenOpen(false)
            await refetchAvail()
          }}
          isLoading={isBookAppointmentPending}
          header={t('Selected time is no longer available')}
          body={t(
            'Unfortunately, the selected time has just been booked by someone else. Please choose another time.',
          )}
          okLabel={t('OK')}
        />
      )}

      {confirmed && (
        <ConfirmationView
          title={t('Your booking is confirmed')}
          partnerServiceName={info?.partnerService.name}
          nameLine={`${form.firstName || '—'} ${form.secondName || ''}`}
          dateTimeLine={`${confirmedDateLegend || selectedDateLegend}${
            confirmedTime || selectedTime
              ? ` - ${confirmedTime || selectedTime}`
              : ''
          }`}
          email={form.email}
          phone={form.phone}
          paragraphs={[
            t(
              'You will immediately receive a confirmation email at the registered address with the appointment confirmation.',
            ),
            t(
              'If you need to cancel or reschedule, the email includes a link to manage those actions.',
            ),
            t(
              'If the registered email is not correct, please contact {{partnerName}} via {{partnerEmail}} or {{partnerPhone}}',
              {
                partnerName: info?.partner.name || '-',
                partnerEmail: info?.partner.email || '-',
                partnerPhone: info?.partner.phone || '-',
              },
            ),
          ]}
        />
      )}

      <FooterInfo
        partner={{
          address: info?.partner.address,
          phone: info?.partner.phone,
          website: info?.partner.website,
          email: info?.partner.email,
          termsAndConditionsUrl: info?.partner.termsAndConditionsUrl,
          privacyPolicyUrl: info?.partner.privacyPolicyUrl,
        }}
        termsLabel={t('Terms & Conditions')}
        privacyLabel={t('Privacy Policy')}
      />
      <PoweredBy poweredByLabel={t('Powered by')} />
      <div id="modal" />
    </PageWrapper>
  )
}
